import { TJoinedClient } from "@/models/call.center.model";
import { CustomContentProps, closeSnackbar } from "notistack";
import { forwardRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { getJoinMeetingUrl } from "@/utils/paths";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path
            d="M5.56587 4.99779L9.88064 0.684931C10.0398 0.525851 10.0398 0.278392 9.88064 0.119311C9.72149 -0.0397702 9.47392 -0.0397702 9.31477 0.119311L5 4.43217L0.685234 0.119311C0.526083 -0.0397702 0.278515 -0.0397702 0.119363 0.119311C-0.0397878 0.278392 -0.0397878 0.525851 0.119363 0.684931L4.43413 4.99779L0.119363 9.31065C-0.0397878 9.46973 -0.0397878 9.71719 0.119363 9.87627C0.190097 9.94697 0.296198 10 0.402299 10C0.5084 10 0.6145 9.96465 0.685234 9.87627L5 5.56341L9.31477 9.87627C9.3855 9.94697 9.4916 10 9.5977 10C9.7038 10 9.8099 9.96465 9.88064 9.87627C10.0398 9.71719 10.0398 9.46973 9.88064 9.31065L5.56587 4.99779Z"
            fill="#5B718E"
        />
    </svg>
);

interface NewLeadNotificationProps extends CustomContentProps {
    lead: TJoinedClient;
}

const NewLeadNotification = forwardRef<HTMLDivElement, NewLeadNotificationProps>(
    ({ id, lead }, ref) => {
        const navigate = useNavigate();

        const handleClick = useCallback(() => {
            closeSnackbar(id);
            navigate(getJoinMeetingUrl(lead.sessionName));
        }, [id, lead.sessionName, navigate]);

        const handleDismiss = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                closeSnackbar(id);
            },
            [id]
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick();
                }
            },
            [handleClick]
        );

        return (
            <div
                ref={ref}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                role="alert"
                tabIndex={0}
                className={cn(
                    "toast-lead-notification relative h-[155px] w-[350px] rounded-lg p-4 outline-none transition-all duration-200",
                    "cursor-pointer focus-visible:ring-2 focus-visible:ring-[#3CAAF9]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
                    "border border-[rgba(81,81,81,0.40)]",
                    "bg-[linear-gradient(93deg,rgba(60,170,249,0.28)_0.14%,rgba(60,170,249,0.06)_99.26%)] backdrop-blur-[15px]",
                    "shadow-[0_8px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.05)]",
                    "hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(0,0,0,0.35),0_0_0_1px_rgba(60,170,249,0.12)]"
                )}
            >
                <button
                    type="button"
                    aria-label="Cerrar notificación"
                    onClick={handleDismiss}
                    className="absolute right-3 top-3 flex h-6 w-6 cursor-pointer items-center justify-center rounded p-0 transition-colors hover:opacity-80"
                >
                    <CloseIcon />
                </button>

                <div className="mb-3 flex items-center gap-2.5">
                    <Bell size={16} className="text-[#3CAAF9]" />
                    <span
                        className="text-base font-bold leading-6 text-[#3CAAF9]"
                        style={{ fontFamily: "Inter" }}
                    >
                        A new lead joined the lobby
                    </span>
                </div>

                <div
                    className={cn(
                        "flex items-center gap-3 rounded-lg border border-white/[0.10] p-3"
                    )}
                >
                    <Avatar className="h-11 w-11 shrink-0 bg-gradient-to-br from-[#3F7F9E] to-[#4DA8FF]">
                        <AvatarFallback className="bg-transparent text-base font-medium uppercase text-white">
                            {lead.name?.charAt(0) || "?"}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-base font-medium text-white">{lead.name}</div>
                        <span className="block text-base font-medium text-[#F3676F]">
                            Hot Lead
                        </span>
                    </div>
                </div>
            </div>
        );
    }
);

NewLeadNotification.displayName = "NewLeadNotification";

export default NewLeadNotification;
